import { Injectable } from '@angular/core';
import { PdfService } from './pdf.service';
import { ImageService } from './image.service';
import { FileDTO } from '@app-libs/model';
import { ConfigurationClientService } from '../client/configuration-client.service';
import { AuthService } from '../auth/auth.service';
import {
  isValidFileType,
  isValidFileSize,
  isPdfFile,
  isImageFile,
} from '../../utils/file-processing.utils';

@Injectable({
  providedIn: 'root',
})
export class FileImportService {
  constructor(
    private pdfService: PdfService,
    private imageService: ImageService,
    private config: ConfigurationClientService,
    private authService: AuthService
  ) {}
  maxFileSizeMB = 10;
  compressImageSizeMB = 1;

  isValidFileType(file: File): boolean {
    return isValidFileType(file, this.config.allowedFileUploadTypes);
  }

  isValidFileSize(file: File): boolean {
    return isValidFileSize(file, this.maxFileSizeMB);
  }

  async readFileContent(
    file: File,
    compress_image = false
  ): Promise<Partial<FileDTO>> {
    if (!this.isValidFileType(file)) {
      throw new Error(`Invalid file type ${file.type}`);
    }

    if (!this.isValidFileSize(file)) {
      throw new Error(`File size exceeds the ${this.maxFileSizeMB} MB limit`);
    }

    if (isPdfFile(file)) {
      return this.readPdfContent(file);
    } else if (isImageFile(file)) {
      return this.readImageContent(file, compress_image);
    } else {
      throw new Error(`Unsupported file type: ${file.type}`);
    }
  }

  private readPdfContent(file: File): Promise<Partial<FileDTO>> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = async () => {
        const content = reader.result as ArrayBuffer;
        const userId = this.authService.getCurrentUserId();
        try {
          const text = await this.pdfService.extractTextFromPDF(content);
          resolve({
            content: text,
            type: file.type,
            user_id: userId,
            file_handle: file,
            file_name: file.name,
            file_path: '', // Add appropriate file path if available
            file_mime: file.type,
            file_size: file.size,
          });
        } catch (error) {
          reject(error);
        }
      };
      reader.readAsArrayBuffer(file);
    });
  }

  private readImageContent(
    file: File,
    compress_image = false
  ): Promise<Partial<FileDTO>> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = async () => {
        try {
          let compressedFile = file;
          if (compress_image) {
            compressedFile = await this.imageService.compressImage(
              file,
              this.compressImageSizeMB
            );
          }

          const base64 = await this.imageService.convertToBase64(
            compressedFile
          );
          const userId = this.authService.getCurrentUserId();

          resolve({
            type: file.type,
            user_id: userId,
            content: base64,
            file_handle: compressedFile,
            file_name: compressedFile.name,
            file_mime: compressedFile.type,
            file_size: compressedFile.size,
          });
        } catch (error) {
          reject(error);
        }
      };
      reader.readAsDataURL(file);
    });
  }
}
